import deviceProfiler from '@narvar/device-profiler';
import { useCallback } from 'react';
import { logger } from '@/lib/logger';
import { NoflakePayload } from '.';

export enum NarvarEventTypes {
  WEB_VITAL = 'web_vital',
}

const getDefaultValues = async (): Promise<NoflakePayload> => {
  const dateTime = new Date().toISOString();
  const {
    location: { href: pageUrl },
  } = window;
  const profiler = await deviceProfiler;

  return {
    tag: 'noflake.consumer_preferences',
    event_ts: dateTime,
    ingestion_timestamp: dateTime,
    page_url: pageUrl,
    event_type: NarvarEventTypes.WEB_VITAL,
    event_name: null,
    web_vital: null,
    web_vital_value: null,
    metric_name: null,
    metric_value_number: null,
    profile_id: null,
    is_verified_email: null,
    is_verified_sms: null,
    client_browser_fingerprint: profiler.browser_fingerprint,
    client_browser_name: profiler.browser_name,
    client_browser_version: profiler.browser_version,
    client_browser_language: profiler.language,
    client_device_mem_gb: profiler.device_mem_gb,
    client_device_model: profiler.device_model,
    client_device_net_speed_mbps: profiler.device_net_speed_mbps,
    client_device_net_type: profiler.device_net_type,
    client_device_num_processors: profiler.device_num_processors,
    client_device_size_px: profiler.device_size_px,
    client_device_touchpoints: profiler.device_touchpoints,
    client_device_type: profiler.device_type,
    client_device_vendor: profiler.device_vendor,
    client_device_os_name: profiler.os_name,
    client_device_os_version: profiler.os_version,
    client_device_time_offset_mins_to_utc: profiler.time_offset_mins_to_utc,
  };
};

type RequiredFields = Pick<NoflakePayload, 'event_name' | 'event_type'>;

export const useAnalytics = () => {
  const postToNoflake = useCallback(
    async (
      host: string,
      data: Partial<NoflakePayload> & Required<RequiredFields>,
    ): Promise<void> => {
      const defaultVaues = await getDefaultValues();
      const providedData: NoflakePayload = {
        ...defaultVaues,
        ...data,
      };

      try {
        await fetch(host, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(providedData),
        });
      } catch (error) {
        logger.error(error, 'Error sending data to noflake');
      }
    },
    [],
  );

  return {
    logEvent: postToNoflake,
  };
};
